import { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { SKILLS } from '../../constants';
import { Reveal, TextReveal } from '../../components/Reveal';
import './DriftWall.css';

// Adapted from React Bits DriftWall. Each skill appears once so every tile
// remains reachable by keyboard and screen reader.
const COLUMN_COUNT = 5;
const COLUMNS = Array.from({ length: COLUMN_COUNT }, (_, column) =>
  SKILLS.filter((_, index) => index % COLUMN_COUNT === column)
);

const SKILL_PROJECTS = {
  Java: 'Paninda Mobile',
  Kotlin: 'Paninda Mobile',
  'Spring Boot': 'Paninda Mobile',
  PostgreSQL: 'Paninda Mobile',
  React: 'AlgoSensei',
  Laravel: 'Autopilot',
};

export const TechStackSection = () => {
  const [activeSkill, setActiveSkill] = useState(SKILLS[0]);
  const [hoveredSkill, setHoveredSkill] = useState(null);
  const wallRef = useRef(null);
  const planeRef = useRef(null);
  const frameRef = useRef(null);
  const reduceMotion = useReducedMotion();

  useEffect(() => () => window.cancelAnimationFrame(frameRef.current), []);

  const moveWall = (event) => {
    if (reduceMotion || !wallRef.current || !planeRef.current) return;
    const rect = wallRef.current.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width - 0.5;
    const y = (event.clientY - rect.top) / rect.height - 0.5;
    window.cancelAnimationFrame(frameRef.current);
    frameRef.current = window.requestAnimationFrame(() => {
      planeRef.current?.style.setProperty('--drift-turn', `${-8 + x * 5}deg`);
      planeRef.current?.style.setProperty('--drift-tilt', `${10 - y * 4}deg`);
    });
  };

  const resetWall = () => {
    setHoveredSkill(null);
    window.cancelAnimationFrame(frameRef.current);
    planeRef.current?.style.setProperty('--drift-turn', '-8deg');
    planeRef.current?.style.setProperty('--drift-tilt', '10deg');
  };

  return (
    <section id="techstack" className="pf-section pf-tech-section">
      <div className="pf-container">
        <div className="pf-tech-showcase pf-drift-showcase">
          <div className="pf-tech-intro">
            <Reveal as="span" className="pf-section-index" distance={6}>03 / Toolbox</Reveal>
            <TextReveal delay={0.08}>What I build with.</TextReveal>
            <Reveal as="p" delay={0.16}>The tools behind my projects.</Reveal>
          </div>

          <div
            ref={wallRef}
            className="pf-drift-wall"
            role="group"
            aria-label="Tech stack skills"
            onPointerMove={moveWall}
            onPointerLeave={resetWall}
          >
            <div ref={planeRef} className="pf-drift-plane">
              {COLUMNS.map((column, columnIndex) => (
                <div className="pf-drift-column" key={columnIndex}>
                  <div className="pf-drift-track">
                    {column.map((skill) => {
                      const Icon = skill.icon;
                      return (
                        <button
                          key={skill.name}
                          type="button"
                          className="pf-drift-tile"
                          aria-label={skill.name}
                          aria-pressed={activeSkill.name === skill.name}
                          data-hovered={hoveredSkill === skill.name || undefined}
                          onPointerEnter={() => setHoveredSkill(skill.name)}
                          onPointerLeave={() => setHoveredSkill(null)}
                          onFocus={() => setHoveredSkill(skill.name)}
                          onBlur={() => setHoveredSkill(null)}
                          onClick={() => setActiveSkill(skill)}
                        >
                          <span className="pf-drift-tile-content">
                            {skill.logo ? (
                              <img src={skill.logo} alt="" className="pf-drift-logo pf-drift-logo-java" />
                            ) : (
                              <Icon className="pf-drift-logo" color={skill.color} aria-hidden="true" />
                            )}
                            <span className="pf-drift-name" aria-hidden="true">{skill.name}</span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <motion.div
            className="pf-tech-detail pf-drift-detail"
            key={activeSkill.name}
            role="status"
            initial={reduceMotion ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: reduceMotion ? 0 : 0.25 }}
          >
            <span className="pf-tech-detail-marker" aria-hidden="true" />
            <strong>{activeSkill.name}</strong>
            <span>{activeSkill.detail}</span>
            {SKILL_PROJECTS[activeSkill.name] && <span className="pf-tech-context">Used in {SKILL_PROJECTS[activeSkill.name]}</span>}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
